import { useEffect, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhysicalClientStatus } from "@/lib/api";
import { STATUS_ACTION_LABELS, STATUS_LABELS } from "./physicalClientLabels";

export const STATUS_COMMENT_REQUIRED = "Informe o motivo da mudança de situação.";

interface PhysicalClientStatusDialogProps {
  /** Situação de destino; null fecha o diálogo. */
  target: PhysicalClientStatus | null;
  clientName: string;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (status: PhysicalClientStatus, comment: string) => Promise<unknown>;
}

const DESCRIPTIONS: Record<PhysicalClientStatus, string> = {
  Active: "O cliente volta a aparecer como ativo na lista.",
  Inactive: "O cliente deixa de aparecer na lista de ativos. O cadastro e o histórico são mantidos.",
  Blocked: "O cliente fica bloqueado. O cadastro e o histórico são mantidos.",
};

/** Mudança de situação com motivo obrigatório (vai para o histórico do cliente). */
export function PhysicalClientStatusDialog({
  target,
  clientName,
  isSubmitting,
  onOpenChange,
  onConfirm,
}: PhysicalClientStatusDialogProps) {
  const commentId = useId();
  const errorId = useId();
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target) {
      setComment("");
      setError(null);
    }
  }, [target]);

  const confirm = async () => {
    if (!target) return;
    if (!comment.trim()) {
      setError(STATUS_COMMENT_REQUIRED);
      return;
    }
    await onConfirm(target, comment.trim());
  };

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        {target && (
          <>
            <DialogHeader>
              <DialogTitle>
                {STATUS_ACTION_LABELS[target]} {clientName}?
              </DialogTitle>
              <DialogDescription>
                Nova situação: {STATUS_LABELS[target]}. {DESCRIPTIONS[target]}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor={commentId}>Motivo</Label>
              <Textarea
                id={commentId}
                value={comment}
                maxLength={500}
                rows={3}
                aria-invalid={!!error}
                aria-describedby={error ? errorId : undefined}
                onChange={(event) => {
                  setComment(event.target.value);
                  if (error) setError(null);
                }}
              />
              {error && (
                <p id={errorId} className="text-sm font-medium text-destructive">
                  {error}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button
                variant={target === "Active" ? "default" : "destructive"}
                onClick={() => void confirm()}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Salvando..." : STATUS_ACTION_LABELS[target]}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
